// src/components/AudionAnalysisPanel/index.tsx
import React from "react";
import * as S from "./AudioAnalysisPanel.styles";
import { PanelContainer } from "../ConversationPanel/ConversationPanel.styles";
import {
  ActionType,
  CommonActionBar,
  DEFAULT_ACTION_STYLES,
} from "../../components/ActionBar";
import { designTokens } from "styles/designTokens";
// import { useProcessVuStore } from "../../../../../store/processVuStore"; // 삭제
import { formatSpeakerName } from "utils/formatSpeaker";
import { ProcessResult as VUResult, Utterance } from "types/output_vu";

export interface Participant {
  label: string;
  percent: number; // 0 ~ 100
  color: string;
}

interface AudioAnalysisPanelProps {
  result: VUResult | null;
  onCopy: () => void;
  onShare: () => void;
  onEdit: () => void;
  onDownload: () => void;
}

const AudioAnalysisPanel: React.FC<AudioAnalysisPanelProps> = ({
  result,
  onCopy,
  onShare,
  onEdit,
  onDownload,
}) => {
  // const { result } = useProcessVuStore(); // 삭제

  const rawKeywords = result?.output.mllm?.keywords || [];
  const keywords =
    typeof rawKeywords === "string" ? rawKeywords.split(", ") : rawKeywords;

  const summaryText = result?.output.mllm?.summary;
  const utterances = result?.output.utterances || [];

  // const leftActions = [
  //   {
  //     type: "copy" as ActionType,
  //     label: "복사",
  //     onClick: onCopy,
  //     ...DEFAULT_ACTION_STYLES.copy,
  //   },
  //   {
  //     type: "share" as ActionType,
  //     label: "공유",
  //     onClick: onShare,
  //     ...DEFAULT_ACTION_STYLES.share,
  //   },
  //   {
  //     type: "edit" as ActionType,
  //     label: "수정",
  //     onClick: onEdit,
  //     ...DEFAULT_ACTION_STYLES.edit,
  //   },
  // ];

  const rightActions = [
    {
      type: "download" as ActionType,
      label: "다운로드",
      onClick: onDownload,
      ...DEFAULT_ACTION_STYLES.download,
    },
  ];

  const participants = React.useMemo(() => {
    if (!utterances || utterances.length === 0) {
      return [];
    }

    const speakerCounts = utterances.reduce(
      (acc: Record<string, number>, utt: Utterance) => {
        const speaker = formatSpeakerName(utt.speaker || "알 수 없는 화자");
        acc[speaker] = (acc[speaker] || 0) + 1;
        return acc;
      },
      {}
    );

    const totalUtterances = utterances.length;
    const colors = [
      designTokens.colors.index.blue,
      designTokens.colors.index.teal,
      designTokens.colors.index.orange,
      designTokens.colors.index.pink,
      designTokens.colors.index.gray,
      designTokens.colors.index.green,
      designTokens.colors.index.purple,
    ];
    let colorIndex = 0;

    return Object.entries(speakerCounts)
      .sort(
        ([, countA]: [string, number], [, countB]: [string, number]) =>
          countB - countA
      )
      .map(([label, count]): Participant => {
        const participant = {
          label,
          percent: Math.round((count / totalUtterances) * 100),
          color: colors[colorIndex % colors.length],
        };
        colorIndex++;
        return participant;
      });
  }, [utterances]);

  return (
    <PanelContainer>
      <S.ContentContainer>
        {/* 키워드 추출 */}
        <S.SectionContainer>
          <S.SectionTitle>키워드 추출</S.SectionTitle>
          <S.TagList>
            {keywords.map((kw: string) => (
              <S.Tag key={kw}>{kw.startsWith("#") ? kw : `#${kw}`}</S.Tag>
            ))}
          </S.TagList>
        </S.SectionContainer>

        {/* 요약 */}
        <S.SectionContainer>
          <S.SectionTitle>요약</S.SectionTitle>
          <S.SummaryItem>{summaryText}</S.SummaryItem>
        </S.SectionContainer>

        {/* 화자별 대화 참여도 */}
        <S.SectionContainer>
          <S.SectionTitle>화자별 대화 참여도</S.SectionTitle>
          <S.ParticipationBar>
            {participants.map((p) => (
              <S.Segment key={p.label} width={p.percent} color={p.color}>
                <span className="label">{p.label}</span>
                <span className="percent">{p.percent}%</span>
              </S.Segment>
            ))}
          </S.ParticipationBar>
        </S.SectionContainer>
      </S.ContentContainer>
      {/* 액션 버튼 */}
      <CommonActionBar leftActions={[]} rightActions={rightActions} />
    </PanelContainer>
  );
};

export default AudioAnalysisPanel;
